import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Play, Eye, ArrowLeft, Loader2, Sparkles, Clock, Download,
  Languages, Share2, Copy, Check,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const SUB_LANGS = [
  { code: "en", label: "English" },
  { code: "it", label: "Italiano" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
];

/**
 * Phase 27c · Public video detail V2 with HTML5 MP4 player + subtitle selector +
 * watch analytics beacon + JSON-LD VideoObject V2 (server-rendered via seo_public_urls).
 * Route: /video/:id
 */
export default function VideoDetail() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subLang, setSubLang] = useState("");
  const [copied, setCopied] = useState(false);
  const [related, setRelated] = useState([]);
  const videoRef = useRef(null);
  const beaconThrottleRef = useRef({ start: 0, progress: 0, complete: 0 });
  const sessionIdRef = useRef(null);

  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = `sess-${Math.random().toString(36).slice(2, 12)}-${Date.now()}`;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/videos/${encodeURIComponent(id)}`, {
        credentials: "include",
      });
      if (r.status === 404) {
        setError("not_found");
        setLoading(false);
        return;
      }
      if (!r.ok) throw new Error(`http_${r.status}`);
      const data = await r.json();
      setVideo(data);
      // Related: fetch creator videos (Feed V2 aware)
      if (data.virtual_creator_id) {
        fetch(`${API}/api/videos/creator/${encodeURIComponent(data.virtual_creator_id)}?limit=6&exclude=${encodeURIComponent(id)}`, { credentials: "include" })
          .then((r) => r.ok ? r.json() : { items: [] })
          .then((d) => setRelated((d.items || []).slice(0, 6)))
          .catch(() => {});
      }
      // Fire upsell context — non-blocking, only if user cookie present.
      fetch(`${API}/api/upsell/context/video-view`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ video_id: id, surface: "video_detail" }),
      }).catch(() => {});
    } catch (e) {
      setError(e?.message || "load_error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Watch-event beacon (throttle 10s per event_type — matches backend)
  const beacon = useCallback((event_type, elapsed_sec = 0) => {
    const now = Date.now();
    if (beaconThrottleRef.current[event_type]
        && (now - beaconThrottleRef.current[event_type]) < 10000) return;
    beaconThrottleRef.current[event_type] = now;
    fetch(`${API}/api/videos/v2/${encodeURIComponent(id)}/watch-event`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type, elapsed_sec,
        session_id: sessionIdRef.current,
      }),
    }).catch(() => {});
  }, [id]);

  const onPlay = () => beacon("start", 0);
  const onProgress = () => {
    const v = videoRef.current;
    if (v) beacon("progress", Math.floor(v.currentTime || 0));
  };
  const onEnded = () => {
    const v = videoRef.current;
    if (v) beacon("complete", Math.floor(v.duration || 0));
  };

  const onHdAttempt = () => {
    fetch(`${API}/api/upsell/context/video-hd-attempted`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ video_id: id, surface: "video_detail" }),
    }).catch(() => {});
    try { window.location.href = "/pricing?src=video_hd"; } catch {}
  };

  // SEO — JSON-LD VideoObject V2 injection
  useEffect(() => {
    if (!video) return;
    try {
      const el = document.getElementById("video-jsonld-v2")
        || document.createElement("script");
      el.id = "video-jsonld-v2";
      el.type = "application/ld+json";
      const seoPub = video.seo_public_urls || {};
      const captions = seoPub.captionUrls || {};
      const captionArr = Object.entries(captions).map(([lang, url]) => ({
        "@type": "MediaObject", contentUrl: url,
        encodingFormat: "text/vtt", inLanguage: lang,
      }));
      const jsonld = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: video.title,
        description: video.description || (video.script || "").slice(0, 200),
        thumbnailUrl: seoPub.thumbnailUrl || `${window.location.origin}${API}/api/videos/thumbnail/${video.video_id}.svg`,
        uploadDate: video.created_at,
        duration: `PT${video.duration_sec || 45}S`,
        contentUrl: seoPub.contentUrl || `${window.location.origin}/video/${video.video_id}`,
        embedUrl: `${window.location.origin}/video/${video.video_id}/embed`,
        author: {
          "@type": "Person",
          name: (video.virtual_creator_id || "unknown").replace(/-/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase()),
          description: "AI-generated content",
          url: `${window.location.origin}/creator/${video.virtual_creator_id}/store`,
        },
        publisher: {
          "@type": "Organization",
          name: "Global Lead Suite",
          logo: {
            "@type": "ImageObject",
            url: `${window.location.origin}/logo.png`,
          },
        },
        interactionStatistic: {
          "@type": "InteractionCounter",
          interactionType: { "@type": "WatchAction" },
          userInteractionCount: video.view_count || 0,
        },
        contentRating: "General",
        inLanguage: video.locale || "en",
        ...(captionArr.length > 0 ? { caption: captionArr } : {}),
      };
      el.text = JSON.stringify(jsonld);
      if (!el.parentNode) document.head.appendChild(el);

      document.title = `${video.title} — Global Lead Suite`;
    } catch {}
  }, [video]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
    </div>
  );

  if (error === "not_found" || !video) return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)] flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold mb-2">Video not found</h1>
        <Link to="/videos" className="inline-flex items-center gap-2 text-sm text-[var(--color-primary)]">
          <ArrowLeft className="h-4 w-4" /> Back to videos
        </Link>
      </div>
    </div>
  );

  const seoPub = video.seo_public_urls || {};
  const contentUrl = seoPub.contentUrl;
  const thumbUrl = seoPub.thumbnailUrl
    || `${API}/api/videos/thumbnail/${video.video_id}.svg`;
  const captionUrls = seoPub.captionUrls || {};
  const availableLangs = Object.keys(captionUrls);
  const grad = video.thumbnail_gradient || {};
  const storyboard = Array.isArray(video.storyboard) ? video.storyboard : [];
  const linkedPackAsset = video.render_v2?.linked_marketplace_asset_id;

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)]"
         data-testid="video-detail-page">
      <div className="max-w-5xl mx-auto p-6 md:p-10 space-y-8">
        <Link to="/videos" data-testid="video-detail-back"
              className="inline-flex items-center gap-2 text-xs text-text-secondary hover:text-[var(--color-secondary)]">
          <ArrowLeft className="h-3 w-3" /> All videos
        </Link>

        {/* Player V2 or fallback hero */}
        {contentUrl ? (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            data-testid="video-detail-player"
            className="rounded-3xl overflow-hidden border border-[var(--color-border)] shadow-2xl bg-black">
            <video
              ref={videoRef}
              data-testid="video-html5-player"
              className="w-full h-auto"
              controls
              preload="metadata"
              poster={thumbUrl}
              onPlay={onPlay}
              onTimeUpdate={onProgress}
              onEnded={onEnded}
              crossOrigin="anonymous"
              style={{ aspectRatio: "16/9" }}
            >
              <source src={contentUrl} type="video/mp4" />
              {availableLangs.map((lang) => (
                <track
                  key={lang}
                  src={captionUrls[lang]}
                  kind="subtitles"
                  srcLang={lang}
                  label={SUB_LANGS.find((s) => s.code === lang)?.label || lang}
                  default={lang === subLang}
                />
              ))}
              Your browser doesn't support HTML5 video.
            </video>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            data-testid="video-detail-hero"
            className="relative rounded-3xl overflow-hidden border border-[var(--color-border)] shadow-2xl"
            style={{
              aspectRatio: "16/9",
              background: grad.start_color
                ? `linear-gradient(${grad.angle || 135}deg, ${grad.start_color}, ${grad.end_color || grad.start_color})`
                : "linear-gradient(135deg, #FFFFFF, #CCCCCC)",
            }}>
            <div className="absolute inset-0 bg-black/25" />
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#161616]-500/30 text-[#CCCCCC]-100 border border-[#2A2A2A]-400/40 backdrop-blur">
                <Sparkles className="h-2.5 w-2.5" /> AI Video
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/10 text-white/90 border border-white/20 backdrop-blur">
                <Clock className="h-2.5 w-2.5" /> {video.duration_sec}s
              </span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-20 w-20 rounded-full bg-white/15 backdrop-blur border border-white/30 flex items-center justify-center">
                <Play className="h-8 w-8 text-white ml-1" fill="currentColor" />
              </div>
            </div>
          </motion.div>
        )}

        {/* Subtitle selector */}
        {availableLangs.length > 0 && (
          <div className="flex items-center gap-3 text-sm" data-testid="subtitle-selector-row">
            <Languages className="h-4 w-4 text-[var(--color-secondary)]" />
            <span className="text-xs uppercase tracking-widest text-text-secondary">Subtitles:</span>
            <select
              data-testid="subtitle-selector"
              value={subLang}
              onChange={(e) => setSubLang(e.target.value)}
              className="text-sm bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg px-3 py-1.5 text-[var(--color-text-main)] hover:border-[var(--color-primary)]"
            >
              <option value="">Off</option>
              {availableLangs.map((code) => (
                <option key={code} value={code}>
                  {SUB_LANGS.find((s) => s.code === code)?.label || code}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Title */}
        <div>
          <h1 data-testid="video-detail-title" className="text-3xl md:text-4xl font-semibold tracking-tight mb-3">
            {video.title}
          </h1>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span>By</span>
            <Link to={`/creator/${encodeURIComponent(video.virtual_creator_id)}/store`}
              data-testid="video-creator-link"
              className="text-[var(--color-secondary)] hover:underline">
              {video.virtual_creator_id}
            </Link>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3 w-3" /> <span data-testid="video-view-count">{video.view_count || 0}</span> views
            </span>
            <span>·</span>
            <span>{new Date(video.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {video.description && (
          <p className="text-sm text-text-secondary leading-relaxed max-w-3xl">
            {video.description}
          </p>
        )}

        {/* Actions bar */}
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 md:p-5 flex flex-wrap items-center gap-3" data-testid="video-actions-bar">
          <button
            data-testid="video-hd-download-btn"
            onClick={onHdAttempt}
            className="text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] inline-flex items-center gap-1"
          >
            <Download className="h-3 w-3" /> Download HD
          </button>
          <button
            data-testid="video-share-copy"
            onClick={copyLink}
            className="text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] hover:border-[var(--color-secondary)] inline-flex items-center gap-1"
          >
            {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy link</>}
          </button>
          <Link
            to={`/video/${encodeURIComponent(id)}/embed`}
            data-testid="video-embed-link"
            className="ml-auto text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] hover:border-[var(--color-secondary)] inline-flex items-center gap-1"
          >
            <Share2 className="h-3 w-3" /> Embed
          </Link>
        </section>

        {/* Pack upsell wire */}
        {linkedPackAsset && (
          <Link
            to={`/videos/pack/${linkedPackAsset}`}
            data-testid="pack-upsell-banner"
            className="block rounded-2xl border border-[var(--color-secondary)]/40 bg-gradient-to-r from-[var(--color-primary)]/10 to-[var(--color-secondary)]/10 p-5 hover:border-[var(--color-secondary)] transition-colors"
          >
            <div className="text-xs uppercase tracking-widest text-[var(--color-secondary)] mb-1">
              Premium Pack Available
            </div>
            <div className="text-sm text-[var(--color-text-main)]">
              Get all videos from this creator in a single premium bundle →
            </div>
          </Link>
        )}

        {/* Script */}
        <section data-testid="video-detail-script"
          className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)]">
            AI-generated script
          </div>
          <p className="text-sm text-[var(--color-text-main)] leading-relaxed whitespace-pre-wrap">
            {video.script}
          </p>
        </section>

        {/* Storyboard */}
        {storyboard.length > 0 && (
          <section data-testid="video-detail-storyboard"
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-6 space-y-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--color-secondary)]">
              <Sparkles className="h-3 w-3" /> Storyboard ({storyboard.length} scenes)
            </div>
            <ol className="space-y-3">
              {storyboard.map((scene, i) => (
                <li key={i} data-testid={`video-scene-${i}`}
                  className="flex gap-4 rounded-xl border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 p-4">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-white hover:bg-[#EDEDED] flex items-center justify-center text-xs font-semibold text-black">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-text-secondary mb-1">
                      {scene.timestamp_sec}s · {scene.duration_sec}s
                    </div>
                    <div className="text-sm text-[var(--color-text-main)] mb-1">
                      {scene.description || scene.visual}
                    </div>
                    {scene.voiceover && (
                      <div className="text-xs text-text-secondary italic">
                        “{scene.voiceover}”
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Related videos */}
        {related.length > 0 && (
          <section data-testid="video-related-section"
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)]">
              More by {video.virtual_creator_id}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {related.map((r) => (
                <Link key={r.video_id} to={`/video/${r.video_id}`}
                  data-testid={`related-video-${r.video_id}`}
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-3 hover:border-[var(--color-secondary)] transition-colors">
                  <div className="text-xs text-text-secondary mb-1">{r.duration_sec}s</div>
                  <div className="text-sm line-clamp-2">{r.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {Array.isArray(video.tags) && video.tags.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="video-tags">
            {video.tags.map((t) => (
              <Link key={t} to={`/videos?category=${encodeURIComponent(t)}`}
                className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-text-secondary border border-[var(--color-border)] hover:border-[var(--color-secondary)] hover:text-[var(--color-secondary)] transition-colors">
                #{t}
              </Link>
            ))}
          </div>
        )}

        <div className="text-[10px] text-text-secondary/70 leading-relaxed border-t border-[var(--color-border)] pt-4">
          🤖 <strong>Trasparenza AI</strong> — questo video è stato generato da un persona virtuale (<code>autonomous_ai</code>).
        </div>
      </div>
    </div>
  );
}
